/**
 * VVorms Physics Constants
 */

export const WA_PHYSICS = {
  /**
   * Fixed simulation timestep. Must match the server tick (60Hz): the
   * per-tick walk step is WALK_SPEED / TICK_RATE, so a mismatched client
   * predicts a different walk speed and rubber-bands on every stop.
   */
  TICK_RATE: 60,
  /** Milliseconds per tick */
  TICK_MS: 1000 / 60,
  /** Scale factor: pixels per meter (legacy, kept for compatibility) */
  PIXELS_PER_METER: 20,
  /** Gravity in pixels/second² (matches WA's 0.24 px/tick²) */
  GRAVITY: 600,

  WORM: {
    /** Worm hitbox width in pixels */
    WIDTH: 14,
    /** Worm hitbox height in pixels */
    HEIGHT: 20,
    /** Walk speed in pixels/second */
    WALK_SPEED: 75,
    /** Jump velocity in pixels/second */
    JUMP_VELOCITY: 200,
    /** Forward jump horizontal speed */
    JUMP_FORWARD_SPEED: 80,
    /** Backflip velocity components */
    BACKFLIP_VELOCITY: { x: 15, y: 280 },
    /** Velocity decay factor per tick while grounded */
    FRICTION: 0.85,
    /** Reduced friction when thrown by explosion (slides further) */
    THROWN_FRICTION: 0.95,
    /** Max slope worm can climb (pixels per step) */
    MAX_CLIMB: 8,
    /** Health points */
    MAX_HEALTH: 100,
    /** Fall damage threshold velocity (pixels/second) - 8 pixels/frame * 50 fps */
    FALL_DAMAGE_THRESHOLD: 400,
    /** Fall damage coefficient (WA default is 50) */
    FALL_DAMAGE_COEF: 50,
  },

  GRENADE: {
    BOUNCE_HORIZONTAL: 0.6,
    BOUNCE_VERTICAL: 0.4,
    FRICTION: 0.85,
    DEFAULT_FUSE: 3,
    EXPLOSION_RADIUS: 50,
    MAX_DAMAGE: 50,
  },
  // GunBound-style "Grub" balls: four bouncy spheres fired in a fan. Each
  // is weaker than a grenade (so four can connect) and bounces livelier.
  GRUB: {
    BOUNCE_HORIZONTAL: 0.7,
    BOUNCE_VERTICAL: 0.5,
    FRICTION: 0.88,
    EXPLOSION_RADIUS: 38,
    MAX_DAMAGE: 22,
    /** Knockback scaling: grub balls shove much less than a grenade. */
    PUSH_MULTIPLIER: 0.45,
    COUNT: 4,
    /** Degrees between adjacent balls in the launch fan. */
    SPREAD_STEP_DEG: 5,
  },
  // GunBound-style "Aduka" Shot 2: three tracer shots fired one after the
  // other down the EXACT same path; each impact calls a Thor strike. Weak
  // tracer pops — the Thor strikes (all on the same spot) are the payload.
  ADUKA: {
    EXPLOSION_RADIUS: 22,
    MAX_DAMAGE: 12,
    /** Total shots fired in sequence along the same trajectory. */
    COUNT: 3,
    /** Delay between each tracer shot (ms). */
    SHOT_INTERVAL_MS: 250,
    /** Short beam delay between a tracer's impact and its Thor strike, so the
     *  strike reads as a response to that hit (matches the weather-Thor feel).
     *  Each tracer fires its OWN strike on impact, in rhythm, instead of the
     *  whole burst deferring into three simultaneous strikes at the end. */
    THOR_STRIKE_DELAY_MS: 180,
  },

  // Lightning (Thor's Hammer): an electric ball that arcs, lands, and calls a
  // vertical lightning bolt straight down from directly above the impact (like
  // a Thor zap, but the satellite is right overhead). T1 = one bolt; T2 = a
  // "V" of two bolts splaying onto points either side of the impact.
  LIGHTNING: {
    /** Short delay (ms) between the ball landing and the bolt(s) striking. */
    STRIKE_DELAY_MS: 140,
    /** T1: altitude (px above the impact) the single vertical bolt traces from. */
    BOLT_ALTITUDE: 400,
    /** Bolt blast radius + damage (per bolt). */
    BOLT_RADIUS: 34,
    BOLT_DAMAGE: 55,
    /** T2: each of the two bolts originates this far (px) to the side AND this
     *  far up, so the arm is ~45deg from vertical (offset == rise) and the two
     *  converge INTO the impact point as a wide "V". Bigger = wider + from
     *  farther away. */
    V_REACH: 520,
  },

  // Knight (Thor's Hammer): a no-damage tracer; when it lands, the satellite
  // high above rains 3 swords down onto the landing point (each a strike that
  // craters + damages, blocked by terrain overhead like a Thor zap).
  KNIGHT: {
    /** Number of swords rained on the landing point. */
    COUNT: 3,
    /** Altitude (px above the FIRER) the satellite hovers at - all swords are
     *  fired from this one point (over the player who fired) and converge into
     *  the tracer's landing point, sequentially (like Aduka's rhythm). */
    SATELLITE_ALTITUDE: 520,
    /** Stagger (ms) between successive swords so they arrive clearly spaced
     *  (you can watch each one leave the satellite in turn). */
    STRIKE_INTERVAL_MS: 320,
    /** Per-sword blast radius + damage. */
    SWORD_RADIUS: 30,
    SWORD_DAMAGE: 48,
  },

  // Trico T2 (Thor's Hammer): 3 lettuce balls explode on contact. The center
  // flies a normal arc; the 2 outer balls orbit around it in flight (their
  // landing spread depends on the spin phase at impact).
  TRICO: {
    EXPLOSION_RADIUS: 40,
    MAX_DAMAGE: 42,
    /** Orbit radius (px) of the 2 outer balls around the center. */
    ORBIT_RADIUS: 26,
    /** Orbit angular speed (rad/s) of the outer balls. */
    ORBIT_SPIN: 6,
  },

  // Kalsiddon (Thor's Hammer): a bomb that splits at the TOP of its arc into
  // several homing missiles that rain down onto the nearest enemy.
  KALSIDDON: {
    /** Number of homing missiles spawned at the apex. */
    COUNT: 4,
    /** Cruise speed (px/s) of the released missiles - faster than the worms'
     *  homing missile, which just adds thrust. */
    MISSILE_SPEED: 620,
    /** Max heading turn (rad/s) toward the target - high = accurate, it locks
     *  onto the contact point and hits it dead-on. */
    MISSILE_TURN: 9,
    /** Horizontal spread (px) the missiles are launched across at the apex. */
    SPREAD_X: 70,
    /** Sideways launch speed spread so the missiles fan out before homing. */
    SPREAD_VX: 90,
  },

  // Grub SS (Thor's Hammer): a single electric ball that rolls like the Shot 2
  // balls but PASSES THROUGH tanks. Its fuse only starts once it first hits
  // terrain; for the whole time it overlaps a tank it electrocutes them
  // (damage over time), and when the fuse expires it explodes.
  GRUB_SS: {
    BOUNCE_HORIZONTAL: 0.7,
    BOUNCE_VERTICAL: 0.5,
    FRICTION: 0.88,
    /** Fuse in seconds, counted from the first terrain contact (not launch). */
    FUSE: 3,
    /** Continuous damage per second while the ball overlaps a tank. */
    DOT_PER_SECOND: 50,
    /** Half-extent (px) of the ball used for the DoT overlap test. */
    RADIUS: 8,
    EXPLOSION_RADIUS: 40,
    MAX_DAMAGE: 70,
    /** Weak knockback on the final blast, like the regular grub. */
    PUSH_MULTIPLIER: 0.45,
  },

  // Nak T2 (Thor's Hammer): a subterranean shell. It arcs normally, then on
  // hitting terrain it BURROWS instead of exploding - keeping its horizontal
  // speed while an upward acceleration curves it back up - and erupts the
  // instant it climbs back out into open air.
  NAK: {
    /** Upward acceleration (px/s^2) applied while burrowing, so the shell
     *  decelerates its dive and resurfaces (replaces gravity underground). */
    RISE_ACCEL: 500,
    /** Safety fuse (s): if it never resurfaces it detonates underground. */
    MAX_BURROW_TIME: 4,
    /** Velocity is scaled by this the instant it burrows, so the shell digs in
     *  with reduced force instead of keeping its full impact momentum. */
    BURROW_FORCE_MULTIPLIER: 0.6,
    EXPLOSION_RADIUS: 45,
    MAX_DAMAGE: 85,
  },

  // J.D. ("CakeBot", Thor's Hammer): electric box-bot. Two magnetic shots that
  // explode on contact like a bazooka but bend the knockback: T2 (Magnet
  // Sphere) PULLS worms toward the impact (negative push multiplier - the
  // signature bunging shot), SS (Anti-Magnet) SHOVES them away hard. Knockback
  // runs through the standard explosion push table (EXPLOSION_PUSH_BY_TYPE),
  // which the mode's explosionPushScale (0.25 in GunBound) tones down on top.
  // A NORMAL weapon-sized explosion (crater + damage, comparable to bazooka/
  // nak) does the damage. The magnet is a SEPARATE, WIDER radial force over
  // MAGNET_RADIUS that weakens linearly from the impact (full at the centre ->
  // 0 at the edge): MAGNET_FORCE is the centre velocity - NEGATIVE pulls worms
  // toward the impact (T2 magnet), POSITIVE shoves them away (SS anti-magnet).
  // Kept gentle (it positions tanks, doesn't fling them). The magnet is
  // independent of the mode's explosionPushScale (J.D.'s signature).
  JD: {
    LAUNCH_SPEED: 750,
    T2_EXPLOSION_RADIUS: 44,
    T2_MAX_DAMAGE: 50,
    T2_MAGNET_RADIUS: 120,
    T2_MAGNET_FORCE: -240,
    SS_EXPLOSION_RADIUS: 54,
    SS_MAX_DAMAGE: 80,
    SS_MAGNET_RADIUS: 150,
    SS_MAGNET_FORCE: 270,
  },

  // Aduka SS (Thor's Hammer): a single phase shot that passes through BOTH
  // terrain and tanks, dealing a solid one-time burst to every enemy tank it
  // flies through, then sails off the map. A mix of Nak (ignore terrain) and
  // Grub SS (pierce worms), but a burst-per-tank instead of damage-over-time.
  ADUKA_SS: {
    /** One-time damage dealt to each enemy tank the shot passes through. */
    DAMAGE_PER_TANK: 70,
    /** Half-extent (px) of the shot used for the pierce overlap test. */
    RADIUS: 8,
  },

  BAZOOKA: {
    SPEED_MULTIPLIER: 3,
    WIND_SUSCEPTIBILITY: 1.0,
    EXPLOSION_RADIUS: 45,
    MAX_DAMAGE: 35,
  },

  DYNAMITE: {
    FUSE_TIME: 5,
    EXPLOSION_RADIUS: 75,
    MAX_DAMAGE: 75,
  },

  CLUSTER_BOMB: {
    BOUNCE_HORIZONTAL: 0.9,
    BOUNCE_VERTICAL: 0.5,
    FRICTION: 0.96,
    DEFAULT_FUSE: 3,
    EXPLOSION_RADIUS: 35,
    MAX_DAMAGE: 30,
    CLUSTER_COUNT: 5,
    CLUSTER_RADIUS: 25,
    CLUSTER_DAMAGE: 25,
  },

  SHOTGUN: {
    PELLET_COUNT: 2,
    SPREAD_ANGLE: 5,
    RANGE: 200,
    DAMAGE_PER_PELLET: 13,
    KNOCKBACK: 150,
  },

  EXPLOSION: {
    /** Damage falloff: damage = maxDamage * (1 - distance/radius) */
    DAMAGE_FALLOFF: true,
    /** Push force multiplier */
    PUSH_FORCE: 2.5,
  },

  COLLISION: {
    /** Direct hit damage multiplier (e.g., 1.5x for bazooka hitting worm directly) */
    DIRECT_HIT_DAMAGE_MULTIPLIER: 1.5,
    /** Push force when worms collide with each other */
    WORM_WORM_PUSH_FORCE: 120,
    /** Minimum velocity for worm-worm collision to apply push */
    WORM_COLLISION_VELOCITY_THRESHOLD: 20,
    /** Bounce factor when grenade hits worm */
    GRENADE_WORM_BOUNCE: 0.5,
  },

  TERRAIN_FRICTION: {
    dirt: 0.85,
    jungle: 0.85,
    desert: 0.9,
    hell: 0.88,
    snow: 0.96,
    alien: 0.92,
  } as Record<string, number>,

  WORLD: {
    /** Default map width */
    WIDTH: 1920,
    /** Default map height */
    HEIGHT: 1080,
    /** Water level from bottom */
    WATER_LEVEL: 100,
    /** Wind range (-10 to +10) */
    MAX_WIND: 10,
    /** Extra space on each side for camera to pan into (water area) */
    CAMERA_PADDING: 400,
  },

  TURN: {
    /** Turn duration in seconds */
    DURATION: 45,
    /** Retreat time after firing */
    RETREAT_TIME: 5,
    /** Time between turns */
    BETWEEN_TURNS: 3,
  },
} as const;

export type Vector2 = { x: number; y: number };

export function vectorAdd(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x + b.x, y: a.y + b.y };
}

export function vectorSub(a: Vector2, b: Vector2): Vector2 {
  return { x: a.x - b.x, y: a.y - b.y };
}

export function vectorScale(v: Vector2, s: number): Vector2 {
  return { x: v.x * s, y: v.y * s };
}

export function vectorLength(v: Vector2): number {
  return Math.sqrt(v.x * v.x + v.y * v.y);
}

export function vectorNormalize(v: Vector2): Vector2 {
  const len = vectorLength(v);
  if (len === 0) return { x: 0, y: 0 };
  return { x: v.x / len, y: v.y / len };
}

export function vectorDistance(a: Vector2, b: Vector2): number {
  return vectorLength(vectorSub(a, b));
}

export function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function radToDeg(radians: number): number {
  return (radians * 180) / Math.PI;
}
